import React, { useCallback, useMemo, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { EBlockType } from 'common/TemplateStudio.types';
import { MERGEABLE_BODY_BLOCKS } from 'constants/bodyBlocks';
import { StoreBodyBlock } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';

import { cn } from 'components/lib/utils';
import {
  buildOnePlusManyV2ItemId,
  getMultiInstanceFormComponent
} from 'components/TemplatesStudio/multiInstanceBlocks';
import {
  IFormStoreBlock,
  IOnePlusManyFormStoreBlock
} from 'components/TemplatesStudio/multiInstanceBlocks/multiInstanceBlocks.types';
import {
  E_BLOCK_TYPE_TO_STORE_BODY_BLOCK,
  isRemovableStoreBlock
} from 'components/TemplatesStudio/storeBlocks/storeBlocks.utils';

import { TemplateStudioBundleForm } from './BlocksForms/Bundle';
import { TemplateStudioDailyBonusForm } from './BlocksForms/DailyBonus';
import { TemplateStudioPromotionForm } from './BlocksForms/Promotion';
import { TemplateStudioRewardCalendarForm } from './BlocksForms/RewardCalendar';
import { TemplateStudioRichTextForm } from './BlocksForms/RichText';
import { TemplateStudioRollingOffersForm } from './BlocksForms/RollingOffers';
import { TemplateStudioTriggeredPopupsForm } from './BlocksForms/TriggeredPopups';
import { DesignBlockMenuItem } from './Components/DesignBlockMenuItem';
import { FormsSeparator, MergeSeparator } from './Components/FormMenuSeparator';
import { SortableBlockItem } from './Components/SortableBlockItem';

const createMapBlockToFormComponent = (): Partial<
  Record<StoreBodyBlock, React.ReactNode>
> => ({
  [StoreBodyBlock.specialOffers]: <TemplateStudioPromotionForm />,
  [StoreBodyBlock.bundles]: <TemplateStudioBundleForm />,
  [StoreBodyBlock.rollingOffers]: <TemplateStudioRollingOffersForm />,
  [StoreBodyBlock.richText]: <TemplateStudioRichTextForm />,
  [StoreBodyBlock.dailyBonusBadge]: <TemplateStudioDailyBonusForm />,
  [StoreBodyBlock.rewardCalendar]: <TemplateStudioRewardCalendarForm />,
  [StoreBodyBlock.triggeredPopups]: <TemplateStudioTriggeredPopupsForm />
});

const OnePlusManyInstanceForm = getMultiInstanceFormComponent(
  StoreBodyBlock.onePlusMany
);

const isOnePlusManyBlock = (
  block: IFormStoreBlock
): block is IOnePlusManyFormStoreBlock => block.type === EBlockType.ONE_PLUS_MANY;

const resolveStoreBodyBlock = (block: IFormStoreBlock): StoreBodyBlock | undefined =>
  E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[block.type];

interface ISidebarRow {
  rowId: string;
  blocks: IFormStoreBlock[];
}

const applyMergedToChain = (blocks: IFormStoreBlock[]): IFormStoreBlock[] => {
  if (blocks.length <= 1) {
    return blocks.map((block) => ({ ...block, mergedTo: undefined }));
  }
  const mergedIds = blocks.slice(1).map((block) => block.id);
  return blocks.map((block, index) =>
    index === 0 ? { ...block, mergedTo: mergedIds } : { ...block, mergedTo: undefined }
  );
};

const buildSidebarRows = (storeBlocks: IFormStoreBlock[]): ISidebarRow[] => {
  const rows: ISidebarRow[] = [];
  let index = 0;

  while (index < storeBlocks.length) {
    const group: IFormStoreBlock[] = [storeBlocks[index]];
    let cursor = index;

    while (cursor + 1 < storeBlocks.length) {
      const mergedTo = storeBlocks[index].mergedTo;
      const nextId = storeBlocks[cursor + 1].id;
      if (!mergedTo || !mergedTo.includes(nextId)) {
        break;
      }
      group.push(storeBlocks[cursor + 1]);
      cursor++;
    }

    rows.push({
      rowId: group.map((block) => block.id).join('+'),
      blocks: group
    });
    index = cursor + 1;
  }

  return rows;
};

const flattenSidebarRows = (rows: ISidebarRow[]): IFormStoreBlock[] =>
  rows.flatMap((row) => row.blocks);

const isMergeableBlock = (block: IFormStoreBlock): boolean => {
  if (isOnePlusManyBlock(block)) return false;
  const storeBodyBlock = resolveStoreBodyBlock(block);
  if (storeBodyBlock === undefined) return false;
  return MERGEABLE_BODY_BLOCKS.has(storeBodyBlock);
};

const MergedBlockGroupWrapper: React.FC<{
  children: React.ReactNode;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes?: Record<string, unknown>;
  isAnyDragging?: boolean;
  isCurrentGroupDragged?: boolean;
}> = ({
  children,
  dragHandleListeners,
  dragHandleAttributes,
  isAnyDragging = false,
  isCurrentGroupDragged = false
}) => (
  <div className="group/mergedGroup relative bg-neutral-100 rounded-lg py-1 px-1">
    <span
      {...(dragHandleListeners || {})}
      {...(dragHandleAttributes || {})}
      className={cn(
        'absolute -left-1 top-1/2 -translate-y-1/2 z-10',
        'cursor-grab active:cursor-grabbing transition-opacity',
        'bg-white rounded-full shadow-sm border border-neutral-200',
        'px-0.5 py-3 flex items-center justify-center',
        isAnyDragging
          ? isCurrentGroupDragged
            ? 'opacity-100'
            : 'opacity-0'
          : 'opacity-0 group-hover/mergedGroup:opacity-100'
      )}
    >
      <FontAwesomeIcon
        icon="fa-solid fa-grip-dots-vertical"
        className="text-xs text-base-mutedForeground"
      />
    </span>
    {children}
  </div>
);

export const TemplateStudioDraggableBlockForms: React.FC<{
  defaultBodyBlocksOrder: StoreBodyBlock[];
}> = () => {
  const { resetSidebars } = useTemplateStudioState();
  const { control, setValue, getValues } = useFormContext();
  const storeBlocks = (useWatch({ control, name: 'storeBlocks' }) ||
    []) as IFormStoreBlock[];
  const rows = useMemo(() => buildSidebarRows(storeBlocks), [storeBlocks]);

  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event: DragStartEvent): void => {
    resetSidebars();
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const currentRows = buildSidebarRows(
        (getValues('storeBlocks') || []) as IFormStoreBlock[]
      );
      const oldIndex = currentRows.findIndex((row) => row.rowId === String(active.id));
      const newIndex = currentRows.findIndex((row) => row.rowId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setValue('storeBlocks', flattenSidebarRows(arrayMove(currentRows, oldIndex, newIndex)), {
          shouldValidate: true,
          shouldDirty: true
        });
      }
    }
    setActiveId(null);
  };

  const handleDragCancel = (): void => {
    setActiveId(null);
  };

  const handleRemoveOnePlusMany = useCallback(
    (blockId: string): void => {
      const current = (getValues('storeBlocks') || []) as IFormStoreBlock[];
      if (!isRemovableStoreBlock(current, blockId)) {
        return;
      }
      setValue(
        'storeBlocks',
        current.filter((block) => block.id !== blockId),
        { shouldValidate: true, shouldDirty: true }
      );
      resetSidebars();
    },
    [getValues, resetSidebars, setValue]
  );

  const handleToggleVisibility = useCallback(
    (rowIndex: number): void => {
      const currentRows = buildSidebarRows(
        (getValues('storeBlocks') || []) as IFormStoreBlock[]
      );
      const row = currentRows[rowIndex];
      if (!row) return;
      const nextVisible = !row.blocks.every((block) => block.isVisible);
      const rowIds = new Set(row.blocks.map((block) => block.id));
      const current = (getValues('storeBlocks') || []) as IFormStoreBlock[];
      setValue(
        'storeBlocks',
        current.map((block) =>
          rowIds.has(block.id) ? { ...block, isVisible: nextVisible } : block
        ),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [getValues, setValue]
  );

  const handleMerge = useCallback(
    (rowIndex: number): void => {
      const currentRows = buildSidebarRows(
        (getValues('storeBlocks') || []) as IFormStoreBlock[]
      );
      const currentRow = currentRows[rowIndex];
      const nextRow = currentRows[rowIndex + 1];
      if (!currentRow || !nextRow) return;

      const mergedBlocks = applyMergedToChain([
        ...currentRow.blocks,
        ...nextRow.blocks
      ]);
      const nextRows = [
        ...currentRows.slice(0, rowIndex),
        { rowId: mergedBlocks.map((block) => block.id).join('+'), blocks: mergedBlocks },
        ...currentRows.slice(rowIndex + 2)
      ];
      setValue('storeBlocks', flattenSidebarRows(nextRows), {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [getValues, setValue]
  );

  const handleDetach = useCallback(
    (rowIndex: number, blockId: string): void => {
      const currentRows = buildSidebarRows(
        (getValues('storeBlocks') || []) as IFormStoreBlock[]
      );
      const row = currentRows[rowIndex];
      if (!row || row.blocks.length <= 1) return;

      const detached = row.blocks.find((block) => block.id === blockId);
      if (!detached) return;

      const remaining = row.blocks.filter((block) => block.id !== blockId);
      const detachedIndex = row.blocks.findIndex((block) => block.id === blockId);
      const placeAbove = detachedIndex < Math.floor(row.blocks.length / 2);

      const remainingRow: ISidebarRow = {
        rowId: remaining.map((block) => block.id).join('+'),
        blocks: applyMergedToChain(remaining)
      };
      const detachedRow: ISidebarRow = {
        rowId: detached.id,
        blocks: [{ ...detached, mergedTo: undefined }]
      };

      const nextRows = [
        ...currentRows.slice(0, rowIndex),
        ...(placeAbove
          ? [detachedRow, remainingRow]
          : [remainingRow, detachedRow]),
        ...currentRows.slice(rowIndex + 1)
      ];

      setValue('storeBlocks', flattenSidebarRows(nextRows), {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [getValues, setValue]
  );

  const mapBlockToFormComponent = createMapBlockToFormComponent();
  const items = rows.map((row) => row.rowId);
  const activeRow = activeId
    ? rows.find((row) => row.rowId === activeId)
    : undefined;

  const canMergeAdjacent = (index: number): boolean => {
    const current = rows[index];
    const next = rows[index + 1];
    if (!current || !next) return false;
    if (!current.blocks.every((block) => block.isVisible)) return false;
    if (!next.blocks.every((block) => block.isVisible)) return false;
    return (
      current.blocks.every(isMergeableBlock) && next.blocks.every(isMergeableBlock)
    );
  };

  const renderBlockForm = (block: IFormStoreBlock): React.ReactNode => {
    if (isOnePlusManyBlock(block)) {
      return OnePlusManyInstanceForm ? (
        <OnePlusManyInstanceForm
          blockId={block.id}
          instanceKey={block.instanceKey}
        />
      ) : null;
    }
    const storeBodyBlock = resolveStoreBodyBlock(block);
    if (storeBodyBlock === undefined) return null;
    return mapBlockToFormComponent[storeBodyBlock];
  };

  const renderDesignBlockMenuItem = (
    block: IFormStoreBlock,
    row: ISidebarRow,
    rowIndex: number,
    options: {
      isHidden: boolean;
      isMergedSubBlock?: boolean;
      onDetach?: () => void;
      onRemove?: () => void;
    }
  ): React.ReactElement | null => {
    if (isOnePlusManyBlock(block)) {
      return (
        <DesignBlockMenuItem
          key={block.id}
          className="design-block-menu-item"
          blockType={block.instanceKey}
          isHidden={options.isHidden}
          isDragging={activeId !== null}
          isCurrentItemDragged={activeId === row.rowId}
          isMergedSubBlock={options.isMergedSubBlock}
          onDetach={options.onDetach}
          onRemove={options.onRemove}
          onToggleVisibility={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleToggleVisibility(rowIndex);
          }}
          v2ItemId={buildOnePlusManyV2ItemId(block.id)}
        >
          {renderBlockForm(block)}
        </DesignBlockMenuItem>
      );
    }

    const storeBodyBlock = resolveStoreBodyBlock(block);
    if (storeBodyBlock === undefined) return null;

    return (
      <DesignBlockMenuItem
        key={block.id}
        className="design-block-menu-item"
        blockType={storeBodyBlock}
        isHidden={options.isHidden}
        isDragging={activeId !== null}
        isCurrentItemDragged={activeId === row.rowId}
        isMergedSubBlock={options.isMergedSubBlock}
        onDetach={options.onDetach}
        onToggleVisibility={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleToggleVisibility(rowIndex);
        }}
        v2ItemId={block.id}
      >
        {renderBlockForm(block)}
      </DesignBlockMenuItem>
    );
  };

  return (
    <DndContext
      id="tb-draggable-blocks-container"
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {rows.map((row, index) => {
          const isHidden = !row.blocks.every((block) => block.isVisible);
          const isMergedGroup = row.blocks.length > 1;
          const showMergeSeparator = canMergeAdjacent(index);
          const nextRow = rows[index + 1];
          const nextIsMergedGroup = nextRow !== undefined && nextRow.blocks.length > 1;
          const hideSeparator =
            isMergedGroup || nextIsMergedGroup || showMergeSeparator;

          if (isMergedGroup) {
            return (
              <React.Fragment key={row.rowId}>
                <SortableBlockItem
                  className="tb-sortable-block-item"
                  id={row.rowId}
                  isHidden={isHidden}
                >
                  <MergedBlockGroupWrapper
                    isAnyDragging={activeId !== null}
                    isCurrentGroupDragged={activeId === row.rowId}
                  >
                    {row.blocks.map((block) =>
                      renderDesignBlockMenuItem(block, row, index, {
                        isHidden,
                        isMergedSubBlock: true,
                        onDetach: () => handleDetach(index, block.id)
                      })
                    )}
                  </MergedBlockGroupWrapper>
                </SortableBlockItem>
                {showMergeSeparator ? (
                  <MergeSeparator onMerge={() => handleMerge(index)} />
                ) : null}
              </React.Fragment>
            );
          }

          const block = row.blocks[0];
          if (!block) return null;

          return (
            <React.Fragment key={row.rowId}>
              <SortableBlockItem
                className="tb-sortable-block-item"
                id={row.rowId}
                isHidden={isHidden}
              >
                {renderDesignBlockMenuItem(block, row, index, {
                  isHidden,
                  onRemove:
                    isOnePlusManyBlock(block) &&
                    isRemovableStoreBlock(storeBlocks, block.id)
                      ? () => handleRemoveOnePlusMany(block.id)
                      : undefined
                })}
              </SortableBlockItem>
              {showMergeSeparator ? (
                <MergeSeparator onMerge={() => handleMerge(index)} />
              ) : (
                !hideSeparator && <FormsSeparator />
              )}
            </React.Fragment>
          );
        })}
      </SortableContext>
      <DragOverlay>
        {activeRow && activeRow.blocks.length > 1 ? (
          <div className="cursor-grabbing shadow-lg bg-white z-50">
            <MergedBlockGroupWrapper
              isAnyDragging={true}
              isCurrentGroupDragged={true}
            >
              {activeRow.blocks.map((block) => {
                if (isOnePlusManyBlock(block)) {
                  return (
                    <DesignBlockMenuItem
                      key={block.id}
                      blockType={block.instanceKey}
                      isHidden={!block.isVisible}
                      isDragging={true}
                      isCurrentItemDragged={true}
                      isMergedSubBlock={true}
                      v2ItemId={buildOnePlusManyV2ItemId(block.id)}
                    >
                      {renderBlockForm(block)}
                    </DesignBlockMenuItem>
                  );
                }
                const storeBodyBlock = resolveStoreBodyBlock(block);
                if (storeBodyBlock === undefined) return null;
                return (
                  <DesignBlockMenuItem
                    key={block.id}
                    blockType={storeBodyBlock}
                    isHidden={!block.isVisible}
                    isDragging={true}
                    isCurrentItemDragged={true}
                    isMergedSubBlock={true}
                    v2ItemId={block.id}
                  >
                    {renderBlockForm(block)}
                  </DesignBlockMenuItem>
                );
              })}
            </MergedBlockGroupWrapper>
            <FormsSeparator />
          </div>
        ) : null}
        {activeRow && activeRow.blocks.length === 1 ? (
          <div className="cursor-grabbing shadow-lg bg-white z-50">
            {renderDesignBlockMenuItem(activeRow.blocks[0], activeRow, 0, {
              isHidden: !activeRow.blocks[0].isVisible
            })}
            <FormsSeparator />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
